/**
 * iter413c-14 — TrialBanner
 *
 * Compact countdown banner shown in the sidebar for tenant-scoped users
 * whose tenant has at least one trial licence. Clicking "Aktivovat" opens
 * Stripe Checkout (subscription mode) and on success the licence flips to
 * `active`.
 *
 * Hidden for super_admin (no tenant_id).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Zap, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/v1/licences/billing`;
const authHeader = () => {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export function TrialBanner({ user, compact = false }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.tenant_id) return;
    axios
      .get(`${API}/tenant/${user.tenant_id}/status`, { headers: authHeader() })
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null));
  }, [user?.tenant_id]);

  if (!user?.tenant_id) return null;
  if (!status?.trial) return null;

  const days = status.days_remaining ?? 0;
  const expired = days <= 0;
  const urgent = days <= 3;

  const onUpgrade = async () => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/checkout/${status.trial.licence_id}`,
        { origin_url: window.location.origin },
        { headers: authHeader() },
      );
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nepodařilo se otevřít Stripe Checkout");
      setLoading(false);
    }
  };

  if (compact) {
    return (
      <button
        type="button"
        onClick={onUpgrade}
        title={`Trial: zbývá ${days} dní`}
        data-testid="trial-banner-compact"
        className={`w-full p-2 rounded-lg flex items-center justify-center mt-2 mb-1 transition ${
          expired ? "bg-rose-500/20" : urgent ? "bg-amber-500/20" : "bg-emerald-500/20"
        }`}
      >
        <Zap className={`w-4 h-4 ${expired ? "text-rose-300" : urgent ? "text-amber-300" : "text-emerald-300"}`} />
      </button>
    );
  }

  return (
    <div
      data-testid="trial-banner"
      className={`mx-2 mt-2 mb-1 rounded-xl border p-3 ${
        expired
          ? "bg-rose-500/15 border-rose-500/40"
          : urgent
          ? "bg-amber-500/15 border-amber-500/40"
          : "bg-emerald-500/15 border-emerald-500/40"
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Clock
          className={`w-4 h-4 ${
            expired ? "text-rose-300" : urgent ? "text-amber-300" : "text-emerald-300"
          }`}
        />
        <div className="text-xs font-bold text-white leading-tight">
          {expired ? "Trial vypršel" : `Zbývá ${days} ${days === 1 ? "den" : days < 5 ? "dny" : "dní"} trialu`}
        </div>
      </div>
      <div className="text-[10px] text-slate-300 mb-2 leading-snug">
        {status.trial.bundle === "eshop_pro" ? "E-shop PRO" : status.trial.bundle} ·{" "}
        {expired ? "Aktivuj pro pokračování." : "Aktivuj nyní bez výpadku."}
      </div>
      <button
        type="button"
        onClick={onUpgrade}
        disabled={loading}
        data-testid="trial-banner-upgrade-btn"
        className={`w-full py-1.5 rounded-lg text-[11px] font-bold flex items-center justify-center gap-1 transition disabled:opacity-50 ${
          expired
            ? "bg-rose-500 hover:bg-rose-600 text-white"
            : urgent
            ? "bg-amber-400 hover:bg-amber-500 text-slate-900"
            : "bg-emerald-400 hover:bg-emerald-500 text-slate-900"
        }`}
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
        Aktivovat předplatné
      </button>
    </div>
  );
}
